import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { showToast } from '@/utils/toast';

/** 与后端 TiktokGoodsService.SYNC_PAGE_SIZE / 平台 pageSize 上限对齐 */
const DEFAULT_MAX_PAGES = 200;
const ABS_MAX_PAGES = 500;
const TIKTOK_PAGE_SIZE = 100;
const MAX_SKC = 100;

function parseProductSkcIds(raw) {
  if (!raw || !String(raw).trim()) return [];
  const seen = new Set();
  const out = [];
  for (const part of String(raw).split(/[,，\s\n\r\t;；]+/)) {
    const token = part.trim();
    if (!token) continue;
    if (!/^\d+$/.test(token)) continue;
    const id = Number(token);
    if (!Number.isSafeInteger(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_SKC) break;
  }
  return out;
}

/**
 * TikTok 产品「同步更新」弹窗。
 * 对齐平台 bg.goods.list.get / bg.glo.goods.list.get：
 * - 默认全量：page + pageSize（≤100）翻页
 * - 指定同步：productSkcIds（货品 SKC ID 列表），不是 SHEIN 的 SPU
 */
export default function TiktokProductSyncModal({
  open,
  confirming = false,
  defaultMaxPages = DEFAULT_MAX_PAGES,
  onConfirm,
  onClose,
}) {
  const [mode, setMode] = useState('default'); // default | custom
  const [maxPages, setMaxPages] = useState(String(defaultMaxPages));
  const [skcText, setSkcText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setMode('default');
    setMaxPages(String(defaultMaxPages || DEFAULT_MAX_PAGES));
    setSkcText('');
  }, [open, defaultMaxPages]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !confirming && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirming, submitting, onClose]);

  const productSkcIds = useMemo(() => parseProductSkcIds(skcText), [skcText]);
  const invalidTokens = useMemo(() => {
    if (!skcText || !String(skcText).trim()) return [];
    const bad = [];
    for (const part of String(skcText).split(/[,，\s\n\r\t;；]+/)) {
      const token = part.trim();
      if (!token) continue;
      if (!/^\d+$/.test(token)) bad.push(token);
    }
    return bad.slice(0, 5);
  }, [skcText]);

  if (!open) return null;

  const busy = confirming || submitting;

  const handleConfirm = async () => {
    if (confirming || submittingRef.current) return;
    if (mode === 'custom') {
      if (invalidTokens.length > 0) {
        showToast(
          fixedUiTpl('SKC ID 须为纯数字，已忽略无效项：{0}', invalidTokens.join(fixedUi('、'))),
          'warning',
        );
      }
      if (productSkcIds.length === 0) {
        showToast(fixedUi('指定同步请填写至少一个货品 SKC ID（productSkcId）'), 'error');
        return;
      }
      if (productSkcIds.length > MAX_SKC) {
        showToast(fixedUiTpl('最多指定 {0} 个 SKC', MAX_SKC), 'error');
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      try {
        await onConfirm?.({ productSkcIds });
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
      return;
    }

    const pages = Number(maxPages);
    if (!Number.isFinite(pages) || pages < 1) {
      showToast(fixedUi("请填写有效的同步分页数（≥1）"), 'error');
      return;
    }
    if (pages > ABS_MAX_PAGES) {
      showToast(fixedUiTpl('同步分页数最多 {0}', ABS_MAX_PAGES), 'error');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm?.({
        maxPages: Math.min(ABS_MAX_PAGES, Math.max(1, Math.floor(pages))),
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="tko-batch-modal" role="presentation">
      <button
        type="button"
        className="tko-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        disabled={busy}
        onClick={() => !busy && onClose?.()}
      />
      <div
        className="tko-batch-modal__panel tko-sync-range-modal__panel tko-product-sync-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tko-product-sync-modal-title"
      >
        <div className="tko-batch-modal__head">
          <h2 id="tko-product-sync-modal-title">{fixedUi("产品同步更新")}</h2>
          <button
            type="button"
            className="tko-btn tko-btn--ghost tko-btn--sm"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("关闭")}
          </button>
        </div>
        <div className="tko-batch-modal__body">

          <div className="tko-sync-range-modal__presets" role="group" aria-label={fixedUi("同步方式")}>
            <button
              type="button"
              className={`tko-btn tko-btn--sm ${mode === 'default' ? 'tko-btn--primary' : 'tko-btn--secondary'}`}
              disabled={busy}
              onClick={() => setMode('default')}
            >
              {fixedUi("默认全量")}
            </button>
            <button
              type="button"
              className={`tko-btn tko-btn--sm ${mode === 'custom' ? 'tko-btn--primary' : 'tko-btn--secondary'}`}
              disabled={busy}
              onClick={() => setMode('custom')}
            >
              {fixedUi("指定 SKC")}
            </button>
          </div>
          <div className="tko-sync-range-modal__fields">
            {mode === 'default' ? (
              <label className="tko-field tko-field--block">
                <span className="tko-field__label">
                  {fixedUi('同步分页数')} (pageSize={TIKTOK_PAGE_SIZE})
                </span>
                <input
                  className="tko-input tko-product-sync-modal__input"
                  type="number"
                  min={1}
                  max={ABS_MAX_PAGES}
                  step={1}
                  value={maxPages}
                  disabled={busy}
                  onChange={(e) => setMaxPages(e.target.value)}
                />
              </label>
            ) : (
              <label className="tko-field tko-field--block">
                <span className="tko-product-sync-modal__label-row">
                  <span className="tko-field__label">
                    {fixedUi('货品 SKC ID')} ({fixedUi('最多')} {MAX_SKC})
                  </span>
                  <span className="tko-product-sync-modal__count">{productSkcIds.length}/{MAX_SKC}</span>
                </span>
                <textarea
                  className="tko-input tko-product-sync-modal__input tko-product-sync-modal__spu"
                  rows={6}
                  placeholder={fixedUi("多个 productSkcId 用换行、逗号或空格分隔，例如：5407624918")}
                  value={skcText}
                  required
                  aria-required="true"
                  disabled={busy}
                  onChange={(e) => setSkcText(e.target.value)}
                />
              </label>
            )}
          </div>
        </div>
        <div className="tko-batch-modal__foot">
          <button
            type="button"
            className="tko-btn tko-btn--ghost"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="tko-btn tko-btn--primary"
            disabled={busy || (mode === 'custom' && productSkcIds.length === 0)}
            onClick={handleConfirm}
          >
            {busy ? fixedUi("启动中…") : fixedUi("开始同步")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
